/**
 * `/auth/reset?token=…&tenantSlug=…` — the emailed reset link. Outside the
 * signed-in tree. A finished reset never signs in by itself: the backend ends
 * every session, so it goes to `/login`.
 */
import type { JSX } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { AuthLayout, ResetCompleteForm, useAuthBranding } from "@linkhr/auth/pages";
import { resolveTenant } from "@linkhr/auth";
import { env } from "../env";

const searchSchema = z.object({
  token: z.string().min(1),
  // The link names the company; resolveTenant reads it from the address.
  tenantSlug: z.string().optional(),
});

export const Route = createFileRoute("/auth/reset")({
  validateSearch: searchSchema,
  component: ResetRouteComponent,
});

function ResetRouteComponent(): JSX.Element {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const branding = useAuthBranding(resolveTenant({ fallbackSlug: env.VITE_DEV_TENANT_SLUG }));
  return (
    <AuthLayout branding={branding} product="accounting">
      <ResetCompleteForm
        token={token}
        apiBaseUrl={env.VITE_API_BASE_URL}
        onSuccess={() => void navigate({ to: "/login" })}
        onRequestNewLink={() => void navigate({ to: "/auth/forgot" })}
      />
    </AuthLayout>
  );
}
